import type { McpServer, McpTool } from '../../types/api-types';
import McpToolCard from '../mcp-tool-card';
import {
  getMcpToolName,
  mcpIsUserDisabled,
  mcpNeedsReconnect,
  mcpShowsReconnectActions,
  mcpShowsToolList,
} from '../../lib/mcp-tool-utils';

type RenderMcpExpandedContentProps = {
  mcp: McpServer;
  mcpRefreshingId: number | null;
  panelMessage?: string;
  onRefreshTools: (mcpId: number) => void;
  onUpdateTool: (mcpId: number, toolName: string, disabledTools: string[], status: string) => void;
  onReconnect?: (mcp: McpServer) => void;
};

function RenderMcpExpandedContent({
  mcp,
  mcpRefreshingId,
  panelMessage,
  onRefreshTools,
  onUpdateTool,
  onReconnect,
}: RenderMcpExpandedContentProps) {
  const showReconnectHint = mcpShowsReconnectActions(mcp, panelMessage);

  const connectionNotice = panelMessage ? (
    <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
        Connection issue
      </p>
      <p className="mt-1 text-xs leading-relaxed text-amber-900">{panelMessage}</p>
      {showReconnectHint && onReconnect && (
        <button
          type="button"
          className="mt-2 text-[11px] font-medium text-blue-600 hover:underline"
          onClick={() => onReconnect(mcp)}
        >
          Open MCP settings
        </button>
      )}
    </div>
  ) : null;

  if (mcpShowsToolList(mcp)) {
    return (
      <>
        {connectionNotice}
        <div className="flex justify-end px-1 mb-1">
          <button
            type="button"
            className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
            disabled={mcpRefreshingId === mcp.id}
            onClick={() => onRefreshTools(mcp.id)}
          >
            {mcpRefreshingId === mcp.id ? 'Refreshing…' : 'Refresh tools'}
          </button>
        </div>
        <div className="flex flex-col gap-0.5 w-full min-w-0">
          {mcp.tools.map((toolItem) => {
            const tool = typeof toolItem === 'string' ? ({ name: toolItem } as McpTool) : toolItem;
            const toolName = getMcpToolName(tool);
            const isDisabled = mcp.disabled_tools?.includes(toolName);

            return (
              <McpToolCard
                key={toolName}
                tool={tool}
                isDisabled={!!isDisabled}
                onToggle={() =>
                  onUpdateTool(mcp.id, toolName, mcp.disabled_tools || [], mcp.status)
                }
              />
            );
          })}
        </div>
      </>
    );
  }

  if (mcpIsUserDisabled(mcp.status) && !showReconnectHint) {
    return (
      <>
        {connectionNotice}
        <p className="px-2 py-2 text-xs text-gray-700 leading-relaxed rounded-md bg-gray-50 border border-gray-100">
          MCP is turned off. Use the toggle to turn it back on.
        </p>
      </>
    );
  }

  if (mcpNeedsReconnect(mcp.status) || showReconnectHint) {
    return (
      <>
        {connectionNotice}
        {!panelMessage && (
          <p className="px-2 py-2 text-xs text-amber-900 leading-relaxed rounded-md bg-amber-50 border border-amber-100">
            Could not connect to this MCP server. Use Reconnect to open MCP settings.
          </p>
        )}
      </>
    );
  }

  return (
    <>
      {connectionNotice}
      <p className="px-2 py-2 text-xs text-gray-600 leading-relaxed rounded-md bg-gray-50 border border-gray-100">
        No tools available for this MCP.
      </p>
    </>
  );
}

RenderMcpExpandedContent.defaultProps = {
  panelMessage: undefined,
  onReconnect: undefined,
};

export default RenderMcpExpandedContent;
